<template>
	<div
		id="app"
		ref="appElement"
		data-app-layout
		class="admin admin--app-nav print:block"
		:class="{
			'admin--mobile': isMobile,
			'admin--desktop': !isMobile,
			'admin--nav-collapsed': !navIsOpen,
			'admin--nav-overlay': showNavOverlay,
			'admin--nav-popper': navIsPopper,
			'admin--nav-transitioning': navIsTransitioning,
			'admin--support-open': supportIsVisible,
			'admin--support-pinned': supportIsVisible && supportIsPinned,
			'admin--support-animates': supportIsTransitioning || supportIsVisible,
			'admin--error': hasGlobalErrors,
			'admin--banner-mode': globalStore.isInViewAsUserMode || isUnrestrictedSupportUser,
			'admin--dark': isDark,
			'admin--light': !isDark
		}"
		:data-route="$route.name"
		:style="{
			'--nav-width': (isMobile ? '100vw' : `${navWidth}px`),
			'--support-width': (isMobile ? '100vw' : `${supportWidth}px`)
		}"
		@click="interceptLinkClicks"
	>
		<CAppInsights ref="ai" />
		<CTastefulBranchName />
		<CViewAsUserBanner
			v-if="globalStore.isInViewAsUserMode"
			:displayed-user-name="globalStore?.fullName"
			:displayed-user-avatar="globalStore?.avatar"
		/>
		<UnrestrictedSupportUserBanner
			v-else-if="isUnrestrictedSupportUser"
		/>
		<CGlobalErrors
			v-if="$route.meta.useSettingsLayout"
			:global-store="globalStore"
			:error-filter="settingsGlobalErrorFilter"
		/>
		<AppSettingsLayout
			v-if="$route.meta.useSettingsLayout"
			:global-store="globalStore"
			:is-navigating="isNavigating"
			@agent-payload="handleAgentPayload"
			@show-account-menu="toggleShowAccountOrgFlyout(true)"
		>
			<RouterView @component-loaded="onLoaded" @show-account-menu="toggleShowAccountOrgFlyout(true)"></RouterView>
		</AppSettingsLayout>
		<template v-else-if="!$route.meta.fauxModal">
			<CGlobalErrors
				v-if="$route.meta.showGlobalErrors"
				:global-store="globalStore"
				:error-filter="$route.meta.globalErrorFilter"
				:error-exclude="$route.meta.globalErrorExclude"
			/>
			<CKeyboardHelp />
			<div
				class="app-container print:!translate-x-0 print:h-fit print:!block"
				:class="{ 'overflow-x-hidden' : isMobile && (navIsOpen || supportIsVisible) }"
				data-app-layout
			>
				<CAppNavContainer
					v-if="isGlobalStoreReady"
					:global-store="globalStore"
					class="over"
					@show-account-menu="toggleShowAccountOrgFlyout(true)"
				/>
				<CAppNavSkeleton v-else />
				<template v-if="$route.name === 'home' || $route.name?.includes('organization-settings')">
					<RouterView
						v-show="!isMobile || !navIsOpen"
						class="app-content under"
						:nav-is-open="navIsOpen || supportIsVisible"
						@component-loaded="onLoaded"
						@show-account-menu="toggleShowAccountOrgFlyout(true)"
					>
					</RouterView>
				</template>
				<div v-else class="admin__scroll-container overflow-hidden">
					<div v-if="$route.meta.showFormHeader" class="admin__form-nav-container">
						<FormNavigationHeader
							:global-store="globalStore"
							:is-navigating="isNavigating"
							@show-account-menu="toggleShowAccountOrgFlyout(true)"
						/>
					</div>
					<RouterView
						v-show="!isMobile || !navIsOpen"
						class="app-content under"
						:is-navigating="isNavigating"
						:nav-is-open="navIsOpen"
						@component-loaded="onLoaded"
						@show-account-menu="toggleShowAccountOrgFlyout(true)"
					>
					</RouterView>
				</div>
			</div>
		</template>
		<CAccountOrgFlyout
			v-if="globalStore.user?.Organizations && allowAccountSideBar"
			:show-menu="showAccountOrgFlyout"
			:orgs="globalStore.user.Organizations"
			:org="globalStore.organization"
			:is-global-admin="isAdmin"
			size="m"
			@close-side-menu="toggleShowAccountOrgFlyout(false)"
		/>
		<CContextMenuContainer v-if="contextMenuIsOpen" />
		<CFormLightboxContainer v-if="formLightboxIsOpen" />
		<CEntryDetailsLightboxContainer v-if="entryDetailsLightboxIsOpen" />
		<RouterView v-if="!allowAccountSideBar" class="modal-content scrollbar scrollbar--wide" @component-loaded="onLoaded"></RouterView>
		<CSupportChatWidget v-if="supportAgentEnabled" />
		<CSupportChatTrigger v-if="!isMobile && !hideSupportChat" :plan-level="globalStore.organization?.Plan?.Name" />
		<TimeoutDialog />
		<CPendo ref="pendo" />
	</div>
</template>

<script>
	import { provide, ref, onMounted, onUnmounted, onBeforeUnmount, computed, watch, nextTick } from 'vue';
	import AppSettingsLayout from 'src/layouts/AppSettingsLayout.vue';
	import CKeyboardHelp from 'src/components/navigation/KeyboardHelp.vue';
	import CAppNavContainer from 'src/components/navigation/AppNavContainer.vue';
	import CAppNavSkeleton from 'src/components/navigation/AppNavSkeleton.vue';
	import CGlobalErrors from 'src/components/app/GlobalErrors.vue';
	import CViewAsUserBanner from 'src/components/app/ViewAsUserBanner.vue';
	import UnrestrictedSupportUserBanner from 'src/components/app/UnrestrictedSupportUserBanner.vue';
	import CContextMenuContainer from 'src/components/navigation/context-menu/ContextMenuContainer.vue';
	import { useContextMenuState } from 'src/components/navigation/context-menu/use-context-menu';
	import CFormLightboxContainer from 'src/views/admin/FormLightboxContainer.vue';
	import CEntryDetailsLightboxContainer from 'src/views/entries/EntryDetailsLightboxContainer.vue';
	import { useFormLightboxState } from './utils/use-form-lightbox';
	import { useEntryDetailsLightboxState } from 'src/views/entries/entry-details/use-entry-details-lightbox';
	import cognitoMessenger from 'src/utilities/cognito-messaging';
	import session, { refreshSession } from '@cognitoforms/api/services/session';
	import { updateSessionOrganization, interceptResponses } from '@cognitoforms/api/services/service-request';
	import { websocketConnection } from 'src/utilities/websocket/websocket-connection-factory';
	import { globalStore } from 'src/stores/global-store';
	import CAppInsights from 'src/components/embeds/AppInsights';
	import CPendo from 'src/components/embeds/Pendo.vue';
	import CTastefulBranchName from 'src/components/embeds/TastefulBranchName.vue';
	import TimeoutDialog from 'src/components/navigation/TimeoutDialog.vue';
	import { injectBing } from 'src/layouts/bing';
	import { injectVwo, trackVwoEvent } from 'src/utilities/vwo';
	import { isPlanAndBillingDocument } from 'src/utilities/plan-and-billing-isolation';
	import { getAgentPayload } from '@cognitoforms/api/services/pendo-service';
	import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
	import CAccountOrgFlyout from 'src/components/navigation/AccountAndOrgFlyout.vue';
	import FormNavigationHeader from 'src/components/navigation/FormNavigationHeader.vue';
	import { GlobalError } from '@cognitoforms/types/server-types/core/services/global-error';
	import { useNavState } from 'src/composables/use-nav-state';
	import { useTreeNodes } from 'src/composables/use-tree-nodes';
	import { useViewAsUser } from 'src/composables/use-view-as-user';
	import { useIsMobile } from 'src/composables/use-is-mobile';
	import { useRoute } from 'vue-router/composables';
	import { useOrganizationAuditLog } from 'src/composables/use-organization-audit-log';
	import { useSupportAgent } from 'src/composables/use-support-agent';
	import { isMobileKey, navStateKey, treeNodesKey, isDarkKey, hasCustomThemeKey, viewAsUserKey, localizationResourcesKey, organizationAuditLogKey, builderAgentComposableKey } from 'src/utilities/injection-keys';
	import { getCustomColorsFromCss, isDefaultColors, calculateIsDark, updateCustomTheme, updateTypeface, updateOrgAppearance, updateFavicon, setPageTitleFromCSS, updateWebAppManifest, getOrgName } from 'src/views/admin/utils/org-appearance-utils';
	import { checkRole } from './utils/role-check';
	import debounce from 'src/utilities/debounce';
	import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
	import { ViewType } from '@cognitoforms/types/server-types/forms/model/view-type';
	import { bootstrap } from 'vue-gtag';
	import { showConfirmationMessage } from './utils/confirmation-messages';
	import CSupportChatWidget, { SUPPORT_CHAT_ANIMATION_DURATION } from 'src/components/app/SupportChatWidget.vue';
	import CSupportChatTrigger from 'src/components/app/SupportChatTrigger.vue';
	import { useBuilderAgent } from 'src/composables/use-builder-agent';

	export default {
		components: {
			AppSettingsLayout,
			CKeyboardHelp,
			CAppNavContainer,
			CAppNavSkeleton,
			CGlobalErrors,
			CViewAsUserBanner,
			UnrestrictedSupportUserBanner,
			CAppInsights,
			CPendo,
			CTastefulBranchName,
			TimeoutDialog,
			CAccountOrgFlyout,
			CContextMenuContainer,
			FormNavigationHeader,
			CFormLightboxContainer,
			CEntryDetailsLightboxContainer,
			CSupportChatWidget,
			CSupportChatTrigger
		},
		beforeRouteEnter(to, from, next) {
			try {
				websocketConnection.load();
			}
			catch (e) {}

			// Show login message if it exists
			if (to.query.loginMessage) {
				showConfirmationMessage(decodeURIComponent(to.query.loginMessage));
				// Remove loginMessage from query params
				to.query.loginMessage = undefined;
				next({ ...to, replace: true });
				return;
			}
			next();
		},
		beforeRouteLeave(to, from, next) {
			// Before switching routes, complete current loading indicator immediately. Necessary to end loading indicator's check-iframe-state polling loop,
			// otherwise navigating from an iframe route before its iframe loads to a Vue route would cause polling to never terminate.
			this.onLoaded();
			this.tryNavigate(to, from, next);
		},
		beforeRouteUpdate(to, from, next) {
			const goingToFauxModal = ['builder', 'builder-new', 'publish'].includes(to.name);
			const comingFromFauxModal = ['builder', 'builder-new', 'publish'].includes(from.name);
			this.showFauxModalFadeIn = goingToFauxModal && !comingFromFauxModal;

			// Before switching routes, complete current loading indicator immediately. Necessary to end loading indicator's check-iframe-state polling loop,
			// otherwise navigating from an iframe route before its iframe loads to a Vue route would cause polling to never terminate.
			this.onLoaded();
			this.tryNavigate(to, from, next);
		},
		setup() {
			const route = useRoute();

			const useSystemColors = computed(() => session.value?.enabledFeatures.UseSystemColors);
			const { isEnabled: supportAgentEnabled } = useSupportAgent();

			const appElement = ref(null);

			const isAdmin = computed(() => checkRole(ClientRole.Administrator));
			const allowAccountSideBar = computed(() => route.meta.useSettingsLayout || !route.meta.fauxModal);

			const viewAsUser = useViewAsUser();
			provide(viewAsUserKey, viewAsUser);

			const organizationAuditLog = useOrganizationAuditLog(session);
			provide(organizationAuditLogKey, organizationAuditLog);

			const settingsGlobalErrorFilter = [GlobalError.BillingRestricted];

			const hasGlobalErrors = computed(() => {
				if (route.meta.useSettingsLayout)
					return settingsGlobalErrorFilter.includes(globalStore.organization.CurrentError);
				if (!route.meta.showGlobalErrors)
					return false;
				if (!globalStore.user.IsVerified)
					return true;
				return globalStore.organization.CurrentError !== GlobalError.None;
			});

			const isMobile = useIsMobile(appElement);
			provide(isMobileKey, isMobile);

			const isDark = ref(false);
			const hasCustomTheme = ref(false);
			provide(isDarkKey, isDark);
			provide(hasCustomThemeKey, hasCustomTheme);

			const updateTheme = () => {
				const colors = globalStore.orgAppearanceSettings?.Colors ?? getCustomColorsFromCss();
				hasCustomTheme.value = !isDefaultColors(colors);
				isDark.value = calculateIsDark(hasCustomTheme.value, useSystemColors.value, colors, globalStore.user?.ColorScheme);
				updateOrgAppearance(useSystemColors.value, globalStore.orgAppearanceSettings, globalStore.user?.ColorScheme, globalStore.organization?.Name);
			};

			// Call updateTheme even if globalstore is not ready yet
			// Can safely rely on set css variables when globalStore is not ready
			updateTheme();

			const navState = useNavState(session.value, route, isMobile);
			provide(navStateKey, navState);

			const treeNodes = useTreeNodes();
			provide(treeNodesKey, treeNodes);

			const contextMenuState = useContextMenuState();
			const formLightboxState = useFormLightboxState();
			const entryDetailsLightboxState = useEntryDetailsLightboxState();

			const resizeObserver = new ResizeObserver(() => {
				// Close context menu if dialog is not opened
				if (!contextMenuState.dialogOpened)
					contextMenuState.isOpen = false;
			});

			const isGlobalStoreReady = ref(false);

			const showViewAsUserError = () => {
				viewAsUser.showErrorNotification();
			};

			const resources = ref();
			provide(localizationResourcesKey, resources);
			globalStore.resourcesReady.then(res => (resources.value = res));

			let viewAsUserSubscription = null;
			const isUnrestrictedSupportUser = ref(false);

			const builderAgentComposable = useBuilderAgent();
			provide(builderAgentComposableKey, builderAgentComposable);
			onMounted(async () => {
				nextTick(() => {
					if (!appElement.value)
						return;
					resizeObserver.observe(appElement.value);
				});

				globalStore.isReady.then(() => {
					isGlobalStoreReady.value = true;

					if (session.value?.isSupportUser) {
						const unrestrictedSupportUserCookie = $cookies.get(`cognito.support.unrestricted.${globalStore.organization?.Id}`);
						isUnrestrictedSupportUser.value = unrestrictedSupportUserCookie != null;
						globalStore.setIsRestrictedSupportUser(unrestrictedSupportUserCookie == null);
					}

					updateTheme();

					viewAsUserSubscription = websocketConnection.subscribeToEvents('OriginalUserMeta', async () => {
						nextTick(() => {
							let attempts = 0;

							const checkMismatch = async () => {
								const inViewAsModeOnServer = await viewAsUser.isInViewAsUserModeOnServer();

								// We only need to do something if there is a mismatch between the server view-as state and isInViewAsUserMode
								if (inViewAsModeOnServer !== globalStore.isInViewAsUserMode) {
									viewAsUser.redirectUser(inViewAsModeOnServer);
									return;
								}

								// If no mismatch found and we haven't reached max attempts, try again
								// polling 5 times (every half second for 2.5 seconds)
								if (attempts < 5) {
									attempts++;
									setTimeout(checkMismatch, 500);
								}
							};

							checkMismatch();
						});
					});
				});

				// Handle View As User exceptions for the app
				interceptResponses(async res => {
					if (res.status === 403) {
						try {
							const text = await res.text();
							if (text) {
								const parsed = JSON.parse(text);
								if (parsed?.error === 'View As User')
									viewAsUser.showErrorNotification();
							}
						}
						catch (e) {
							// Do nothing
						}
					}
				});
			});

			onUnmounted(() => {
				document.documentElement.classList.remove('is-mobile');
				resizeObserver.disconnect();
				viewAsUserSubscription?.unsubscribe();
			});

			watch(isMobile, (newValue) => {
				const html = document.documentElement;
				if (newValue)
					html.classList.add('is-mobile');
				else
					html.classList.remove('is-mobile');
			}, { immediate: true });

			// Nav State - Resize Handling
			const handleWindowResize = debounce(() => (navState.handleWindowResize(window.innerWidth)), 150);
			onMounted(() => (window.addEventListener('resize', handleWindowResize)));
			onBeforeUnmount(() => (window.removeEventListener('resize', handleWindowResize)));

			// Nav State - Sidebar
			const navIsTransitioning = ref(false);
			let navTransitionFallbackTimer = null;
			const clearNavTransitionFallback = () => {
				if (navTransitionFallbackTimer) {
					clearTimeout(navTransitionFallbackTimer);
					navTransitionFallbackTimer = null;
				}
			};
			// handleNavTransition (below) is what normally clears this, via transitionend on .app-container's transform.
			// That event doesn't always arrive (e.g. the container isn't mounted/animating for every route the nav can
			// close on), which otherwise leaves .support-and-avatar etc. stuck at opacity 0 indefinitely. Fall back to a
			// timer - matching --nav-speed's .4s duration plus a buffer - so the flag always self-clears.
			const setNavIsTransitioning = value => {
				clearNavTransitionFallback();
				navIsTransitioning.value = value;
				if (value)
					navTransitionFallbackTimer = setTimeout(() => setNavIsTransitioning(false), 600);
			};
			onBeforeUnmount(clearNavTransitionFallback);

			watch(navState.isOpen, newValue => {
				if (!route.name.includes('organization-settings'))
					setNavIsTransitioning(newValue);
			});

			watch(isMobile, () => {
				setNavIsTransitioning(false);
			});

			const navWidth = ref(navState.navWidth.value);
			watch(navState.navWidth, () => {
				requestAnimationFrame(() => {
					navWidth.value = navState.navWidth.value;
				});
			});

			watch(navState.isPopper, () => {
				// When resizing a docked popper on the build/publish page, we don't want the content to move
				if (navState.isPopper.value && navState.showOverlay.value) {
					requestAnimationFrame(() => { // wait until the popper is docked
						appElement.value.style.setProperty('--nav-speed', '0s');
					});
				}
				else
					appElement.value.style.setProperty('--nav-speed', '.4s');
			});

			// Nav State - Support Sidebar

			// Disable animations when the support panel is closed, except during open/close transitions
			const supportIsTransitioning = ref(false);
			watch(navState.isSupportOpen, () => (supportIsTransitioning.value = true));

			// Some routes (e.g. the standalone invoice) suppress the support chat entirely. The widget stays mounted so
			// an in-progress conversation survives the round trip, but it is treated as closed for layout purposes.
			const hideSupportChat = computed(() => !!route.meta.hideSupportChat);
			const supportIsVisible = computed(() => navState.isSupportOpen.value && !hideSupportChat.value);

			const supportWidth = ref(navState.supportWidth.value);
			watch(navState.supportWidth, () => {
				requestAnimationFrame(() => {
					supportWidth.value = navState.supportWidth.value;
				});
			});

			// Nav State - Handle end of nav/support transitions
			const handleNavTransition = e => {
				if (e.propertyName === 'transform' && e.target.classList.contains('app-container'))
					setNavIsTransitioning(false);
				else if (e.srcElement.classList.contains('support-chat-widget'))
					supportIsTransitioning.value = false;
			};

			onMounted(() => (appElement.value.addEventListener('transitionend', handleNavTransition)));
			onBeforeUnmount(() => (appElement.value.removeEventListener('transitionend', handleNavTransition)));

			// Organization Appearance - Change org appearance on upgrade/downgrade
			const allowManageAppearance = computed(() => globalStore.organization?.Plan?.AllowManageOrgAppearance);

			watch(allowManageAppearance, async (newValue, oldValue) => {
				if (oldValue !== undefined && newValue !== oldValue) {
					await globalStore.loadPortalMeta();
					updateTheme();
				}
			});

			return {
				isAdmin,
				allowAccountSideBar,
				hasGlobalErrors,
				settingsGlobalErrorFilter,
				isMobile,
				supportAgentEnabled,
				SUPPORT_CHAT_ANIMATION_DURATION,
				appElement,
				globalStore: globalStore,
				contextMenuIsOpen: computed(() => contextMenuState.isOpen),
				formLightboxIsOpen: computed(() => formLightboxState.open),
				entryDetailsLightboxIsOpen: computed(() => entryDetailsLightboxState.open),
				isGlobalStoreReady,
				updateTheme,
				isDark,
				useSystemColors,
				showViewAsUserError,
				isUnrestrictedSupportUser,
				// Nav State
				navState,
				navWidth,
				navIsOpen: navState.isOpen,
				navIsPopper: navState.isPopper,
				showNavOverlay: navState.showOverlay,
				navIsTransitioning,
				supportWidth,
				supportIsOpen: navState.isSupportOpen,
				supportIsPinned: navState.isSupportPinned,
				supportIsTransitioning,
				hideSupportChat,
				supportIsVisible
			};
		},
		data() {
			return {
				isNavigating: false,
				agentPayload: null,
				title: '',
				unsubscribes: [],
				showAccountOrgFlyout: false,
				organization: null,
				showFauxModalFadeIn: false
			};
		},
		head() {
			return {
				title: this.title,
				htmlAttrs: {
					class: [
						'app',
						'not-iframe-page',
						...(navigator.platform.includes('Win') ? ['is-windows'] : []),
						...(navigator.userAgent.includes('Android') ? ['is-android'] : []),
						...((navigator.platform.includes('iPad') || navigator.platform.includes('iPhone')) ? ['is-ios'] : []),
						...(navigator.platform.includes('Mac') ? ['is-mac'] : [])
					]
				}
			};
		},
		computed: {
		},
		watch: {
			isGlobalStoreReady() {
				this.updateSelectedEntryView();
			},
			'$route.name'() {
				this.updateTitle();
			},
			'$route.fullPath'() {
				this.updateSelectedEntryView();
			},
			'globalStore.organization.Name'() {
				this.updateTitle();
			},
			'globalStore.currentForm.Name'() {
				this.updateTitle();
			},
			'globalStore.currentView.Id'() {
				this.updateTitle();
			},
			'globalStore.currentView.Name'() {
				this.updateTitle();
			},
			'globalStore.orgAppearanceSettings.Colors'() {
				this.updateTheme();
			},
			'globalStore.orgAppearanceSettings.UseDefaultTypeface'() {
				updateTypeface(globalStore.orgAppearanceSettings.UseDefaultTypeface);
			},
			'globalStore.user.ColorScheme'() {
				this.updateTheme();
			},
			'globalStore.orgAppearanceSettings.FaviconLink'() {
				this.updateTitle();
				updateFavicon(globalStore.orgAppearanceSettings?.LogoLink, globalStore.orgAppearanceSettings?.FaviconLink, globalStore.organization?.Name);
			}
		},
		created() {
			this.addMessageHandlers();
			this.updateTitle();
		},
		mounted() {
			// Set up session-sync event handlers
			window.addEventListener('focus', this.updateOrganizationCookie);
			this.unsubscribes.push(() => window.removeEventListener('focus', this.updateOrganizationCookie));
			this.unsubscribes.push(cognitoMessenger.addHandler('window-focused', this.updateOrganizationCookie));

			// Watch for system color changes
			if (session.value?.enabledFeatures.UseSystemColors) {
				const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
				const handleChange = (event) => {
					updateCustomTheme(this.useSystemColors, globalStore.orgAppearanceSettings?.Colors, globalStore.user?.ColorScheme);
				};
				mediaQuery.addEventListener('change', handleChange);
				this.unsubscribes.push(() => mediaQuery.removeEventListener('change', handleChange));
			}

			// Add user session-specific information to App Insights
			this.configureApplicationInsights(session.value);

			// Skip the first frame that starts the transition
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					this.$el.style.setProperty('--nav-speed', '.4s');
				});
			});

			if (session.value.isGuestSession)
				return;

			const isBillingDocument = isPlanAndBillingDocument();

			// Establish VWO identity in SPA so the same anonymous user from marketing is tracked here.
			injectVwo();

			// Log event to Google Analytics, Bing, and VWO when the 'category' and 'source' args are present (i.e. ad conversion tracking)
			bootstrap().then(() => {
				if (this.$route && this.$route.query) {
					const category = this.$route.query.category;
					const source = this.$route.query.source;
					const details = this.$route.query.details;
					if (category && source) {
						this.handleMarketingGoogleAnalytics(category, source, details);
						if (!isBillingDocument)
							this.handleBingAnalytics(source, category, details);
						trackVwoEvent('signup');
					}
				}
			});

			if (!isBillingDocument) {
				// Fetch info from server then configure Pendo
				getAgentPayload().then(agentPayload => {
					this.configurePendo(agentPayload);
					this.handleAgentPayload();
				});
			}
		},
		beforeDestroy() {
			for (const unsubscribe of this.unsubscribes)
				unsubscribe();
		},
		methods: {
			onLoaded() {
				this.isNavigating = false;
			},
			addMessageHandlers() {
				this.unsubscribes = [
					cognitoMessenger.addHandler('navigate', this.navigate),
					cognitoMessenger.addHandler('broadcastToken', this.broadcastToken),
					cognitoMessenger.addHandler('received-session-token', refreshSession),
					cognitoMessenger.addHandler('organizationChanged', this.onOrganizationChanged),
					cognitoMessenger.addHandler('showViewAsUserError', this.showViewAsUserError)
				];
			},
			// Resets the organization cookie to the cached version when the page is focused
			updateOrganizationCookie() {
				const expiration = new Date();
				expiration.setDate(expiration.getDate() + 30);
				this.$cookies.set('cognito.organization', session.value.organizationId + '|' + expiration.toISOString() + '|' + session.value.authMethod, '30d');
			},
			onOrganizationChanged(id) {
				if (id.includes('-'))
					updateSessionOrganization(id);
				else
					updateSessionOrganization(null);

				refreshSession();
				cognitoMessenger.trigger('updateFormsData');
			},
			handleShowLoadingIndicator() {
				this.isNavigating = true;
			},
			async tryNavigate(to, from, next) {
				const navigate = window['Cognito']?.navigate || (() => Promise.resolve());
				await navigate();

				if (!to?.params.sameForm) {
					// Show loading indicator if navigating from one page to another, or if staying on the same page but switching forms
					if (from?.name !== to?.name || !from?.params.formUrlName || !to?.params.formUrlName || from?.params.formUrlName !== to?.params.formUrlName)
						this.handleShowLoadingIndicator();
				}
				// WI #32639: Refresh the page, to force iOS to show the address bar when >= 15 and < 15.4
				const iOSVersion = /iP(hone|ad|od) OS ([0-9_]+(\.[0-9_]+)*)/i.exec(window.navigator.userAgent)?.[2]?.replace(/_/g, '.') || NaN;
				if (iOSVersion && /^15(\.([0-3](\.\d{1,2})?)?)?$/.test(iOSVersion))
					window.location.href = to.fullPath;
				else
					next();
			},
			navigate(nav) {
				if (nav.location) {
					document.location.href = nav.location;
					return;
				}

				// Update the url of the chrome when the underlying view changes
				let chromeUrl = document.location.href.toLowerCase();
				const index = chromeUrl.indexOf('/', 10);
				if (index > 0) {
					// Reduce chrome url to the form "/module/.."
					try {
						chromeUrl = decodeURI(chromeUrl.substring(index));
					}
					catch {
						chromeUrl = chromeUrl.substring(index);
					}

					if (this.$route.name !== this.$router.matcher.match(nav.url).name && !(this.$route.name === 'builder-new' && this.$router.matcher.match(nav.url).name === 'builder')) {
						// If the converted view url is different from the chrome url, push the url into the chrome history
						if (this.$route.fullPath.toLowerCase() !== nav.url.toLowerCase())
							this.$router.push(nav.url);
					}
					else if (nav.url !== chromeUrl)
						this.$router.replace(nav.url);
				}
			},
			// If Cognito is being hosted in an iframe, send session token out
			broadcastToken(token) {
				if (window !== window.top) {
					window.top.postMessage('token: ' + token, '*');
				}
			},
			handleAgentPayload(agentPayload) {
				this.agentPayload = agentPayload;
			},
			updateTitle() {
				if (globalStore.orgAppearanceSettings?.LogoLink) {
					this.title = globalStore.organization.Name + ' - Cognito Forms';
					updateWebAppManifest(globalStore.orgAppearanceSettings?.LogoLink, globalStore.organization.Name);
				}
				else if (!globalStore.organization?.Name) {
					const updatedTitle = setPageTitleFromCSS();
					this.title = updatedTitle || 'Cognito Forms';
					updateWebAppManifest(getComputedStyle(document.documentElement).getPropertyValue('--org-appearance-logo'), getOrgName());
				}
				else {
					this.title = 'Cognito Forms';
					updateWebAppManifest();
				}
				if (this.$route.name === 'forms')
					this.title = 'All Forms - Cognito Forms';
				else if (this.$route.name === 'selectplan')
					this.title = 'Select Plan - Cognito Forms';
				else if (this.$route.name === 'subscription-settings')
					this.title = 'Subscription Settings - Cognito Forms';
				else if (this.$route.name === 'receipt-settings')
					this.title = 'Subscription Receipt - Cognito Forms';
				else if (this.$route.name === 'profile-settings')
					this.title = 'User Profile - Cognito Forms';
				else if (this.globalStore.currentForm && this.$route.name !== 'builder-new') {
					if (this.$route.name.startsWith('builder'))
						this.title = this.globalStore.currentForm.Title + ' - Build';
					else if (this.$route.name === 'publish')
						this.title = this.globalStore.currentForm.Title + ' - Publish';
					else if (this.$route.name === 'entries' && this.globalStore.currentView)
						this.title = this.globalStore.currentForm.Title + ' - ' + this.globalStore.currentView.Name;
				}
			},
			async updateSelectedEntryView() {
				const formName = this.$route.params.formUrlName?.toLowerCase();
				let selectedForm = Object.values(this.globalStore.forms).find(form => form.UrlName.toLowerCase() === formName);

				// See if form is unloaded archived form
				if (formName && !(selectedForm || this.globalStore.hasLoadedArchivedForms)) {
					const formId = this.$route.params.model?.FormId;
					await this.globalStore.loadForms([formId]);
					selectedForm = Object.values(this.globalStore.forms).find(form => form.InternalName.toLowerCase() === formName);

					if (selectedForm)
						this.globalStore.setCurrentForm(formId);
				}

				// Reset entry details nav state if not navigating to entries (entries.js handles this otherwise)
				if (this.$route.name !== 'entries')
					this.navState.setEntryDetailsIsOpen(false);

				const isDeletedView = (this.globalStore.currentForm?.IsDeleted || this.globalStore.currentView?.IsDeleted)
					&& this.globalStore.currentForm?.UrlName?.toLowerCase() === formName
					&& this.globalStore.currentView?.Id?.split('-')[1] === this.$route.params.entryView?.split('-')[0]
					&& this.$route.name === 'entries';

				if (isDeletedView || this.$route.params.lightboxNavigation)
					return;

				if (!selectedForm) {
					this.globalStore.clearCurrentForm();
					return;
				}

				// Set currentForm if on builder or publish, clear currentView
				if (this.$route.name === 'builder' || this.$route.name === 'publish') {
					this.globalStore.clearCurrentView();
					this.globalStore.setCurrentForm(selectedForm.Id);
					return;
				}

				// Check if navigating to entry view on current form before updating selectedForm
				if (this.$route.params.entryView) {
					const viewNumber = this.$route.params.entryView.split('-')[0];
					const viewId = !isNaN(Number(viewNumber)) ? `${selectedForm.Id}-${viewNumber}` : selectedForm.EntryViews[0].Id;

					// Do not set form views as the current views for guest users
					const view = selectedForm.EntryViews.find(v => v.Id === viewId);
					if (this.globalStore.organization.Role === ClientRole.Guest && view?.Type === ViewType.Form)
						return;

					this.globalStore.setCurrentView(viewId);
				}
				// Only set the view if the current view is not for the selected form
				else if (!selectedForm.EntryViews.find(v => v.Id === this.globalStore.currentView?.Id)) {
					this.globalStore.setCurrentView(selectedForm.EntryViews[0].Id);
				}
			},
			handleMarketingGoogleAnalytics(action, category, label) {
				// GA4
				const actionDetails = {
					'event_category': category,
					'event_label': label,
					'send_to': CognitoConfiguration.GoogleAnalyticsId || 'G-TTBCW8BFKB'
				};
				this.$gtag.event(action, actionDetails);
			},
			handleBingAnalytics(source, category, details) {
				injectBing();
				window.uetq = window.uetq || [];
				window.uetq.push('event', 'page_view', { page_path: this.$route.fullPath });
				window.uetq.push('event', source, { event_category: category, event_label: details });
			},
			configurePendo(agentPayload) {
				const pendo = this.$refs.pendo && this.$refs.pendo.pendo;
				if (pendo) {
					pendo.initialize(agentPayload);
				}
			},
			configureApplicationInsights(session) {
				const appInsights = this.$refs.ai?.appInsights;

				if (session?.isAuthenticated && appInsights)
					appInsights.setAuthenticatedUserContext(session.userId, session.organizationId);
			},
			/**
			 * Forces non-vue links matching client-side routes to use client-side navigation.
			 * @param {Event} evt
			 */
			async interceptLinkClicks(evt) {
				function isRouterLink(component) {
					if (component.$options._componentTag === 'router-link')
						return true;

					// Check first child for router link if matches original component $el (ex. IconButton wraps a router-link)
					if (component.$children.length === 1 && component.$children[0].$el === component.$el)
						return isRouterLink(component.$children[0]);

					return false;
				};

				let link = evt.target;
				if (link && link.tagName !== 'A' && typeof link.closest === 'function')
					link = link.closest('a');
				// Do not try to replace if already a router-link
				if (link && link.__vue__ && isRouterLink(link.__vue__))
					return;
				if (link?.matches('#c-admin a[href]:not([target])')) {
					const href = link.href;
					const hrefAttr = link.getAttribute('href');
					if (href && hrefAttr && !hrefAttr.startsWith('#') && !hrefAttr.startsWith('javascript:')) {
						let path = href;
						try {
							const url = new URL(href);
							path = url.pathname + url.search;
						}
						catch (_) { }
						const route = this.$router.match(path);

						if (route && route.name !== '404') {
							evt.preventDefault();
							if (link.hasAttribute('data-route-replace')) {
								// replace the route on the stack so that the back button always returns to the last entries page visited
								this.$router.replace(route);
							}
							else
								this.$router.push(route);
						}
					}
				}
			},
			toggleShowAccountOrgFlyout(showFlyout) {
				this.showAccountOrgFlyout = showFlyout;
			}
		}
	};
</script>

<style lang="scss">
@import '@legacy/Content/required';
@import '@legacy/Content/app';

option {
	background: var(--bg-primary);
}

.app {

	&.is-mobile {
		height: 100%;

		body,
		.admin {
			height: 100%;
		}
	}

	// Support toggling of UI elements for edge cases
	[data-admin-layout] [data-app-layout] {
		display: none !important;
	}

	[data-app-layout] [data-admin-layout] {
		display: none !important;
	}
}

.admin--app-nav {
	display: grid;
	grid-template-rows: 1fr;
	grid-template-columns: 1fr;
	height: 100vh;
	/* stylelint-disable-next-line declaration-block-no-duplicate-properties */
	height: 100dvh;

	&.admin--desktop.admin--nav-collapsed {
		overflow: hidden;
	}

	&.admin--mobile {
		--nav-width: 100vw;
	}

	&.admin--error,
	&.admin--banner-mode {
		grid-template-rows: auto 1fr;
	}

	.app-container {
		display: grid;
		grid-template-columns: var(--nav-width) 1fr;
		grid-template-rows: 100%;
		overflow-y: hidden;
		transition: transform var(--nav-speed);
	}

	.admin__form-nav-container {
		height: 46px;
		margin-top: 5px;
	}

	// Addresses 45530. The centered layout and colorful greeting makes this more of an obvious problem on home page.
	// On the entries page, since the heading is left-aligned and mostly white, it looks better without this tweak.
	.support-and-avatar,
	.org-stats__bar,
	.c-entries-utility {
		transition: opacity .2s;
	}

	&.admin--nav-transitioning .app-content.app-home {
		transform: translateX(calc(var(--nav-width) * -.5));
		transition: transform var(--nav-speed);

		.org-stats__bar {
			opacity: 0;
		}
	}

	&.admin--nav-transitioning {

		.c-entries-utility,
		.support-and-avatar {
			opacity: 0;
		}
	}

	&.admin--nav-collapsed,
	&.admin--nav-transitioning {

		.app-container {
			width: calc(100vw + var(--nav-width));

			@media print {
				width: 100%;
			}
		}
	}

	&.admin--nav-collapsed {

		.app-container {
			transform: translateX(calc(var(--nav-width) * -1));
		}

		.nav-header--collapsed {
			transform: translateX(var(--nav-width));
		}
	}

	.nav-header--collapsed {
		transition: transform var(--nav-speed);
	}

	// Delays resizing of the app container until after the support panel pin animation completes
	@keyframes expand-for-support {

		to {
			grid-template-columns: var(--nav-width) 1fr var(--support-width);
		}
	}

	&.admin--support-pinned .app-container {
		animation: expand-for-support 0s linear v-bind('SUPPORT_CHAT_ANIMATION_DURATION + "ms"') forwards;
	}

	$z-nav: 2;
	$z-content: 1;

	--nav-speed: 0s;
	--cogicon-space: #{$space__5 + $space__3};

	// needs to be dynamic based on width of side menu
	$menuWidthPlusSomeAmount: $b4;
	transition: transform var(--nav-speed);

	.modal-content {
		position: fixed;
		right: 0;
		z-index: $z-content;
	}

	.nav__side-menu {
		transition: var(--nav-speed);
	}

	@include b-down($menuWidthPlusSomeAmount) {

		.nav__side-menu {
			width: 100%;
		}
	}

	@include b-up($menuWidthPlusSomeAmount) {

		&.admin--nav-popper .nav__side-menu {
			box-shadow: 3px 0 13px var(--shadow-color-intense) !important;
			transform: translateX(var(--nav-width));
		}
	}

	&.admin--nav-popper .main-heading {
		transition: none !important;
	}

	.main-heading {
		display: flex;
		align-items: center;
		width: fit-content;
		min-height: $space__5;
		font-family: var(--font-display);
		transition: transform var(--nav-speed);
	}

	.nav-header--collapsed .nav-header__org {
		display: none;
	}

	&.admin--nav-collapsed .nav-header--collapsed .nav-header__org {
		opacity: 0;
		pointer-events: none;
	}

	.faux-modal-loader__intro {
		z-index: $z2;
		transition-duration: .2s;
	}
}
</style>
